import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const sections = [
  { id: 'liveNow', icon: '✅' },
  { id: 'nextUp', icon: '🔥' },
  { id: 'futureBacklog', icon: '🧭' },
  { id: 'notes', icon: '📝' },
];

const sectionAccent = {
  liveNow: {
    background: 'rgba(34, 197, 94, 0.12)',
    border: 'rgba(34, 197, 94, 0.25)',
    text: '#166534',
  },
  nextUp: {
    background: 'rgba(245, 158, 11, 0.14)',
    border: 'rgba(245, 158, 11, 0.28)',
    text: '#92400e',
  },
  futureBacklog: {
    background: 'rgba(99, 102, 241, 0.12)',
    border: 'rgba(99, 102, 241, 0.24)',
    text: '#4338ca',
  },
};

function SummaryCard({ label, value, help }) {
  return (
    <div className="col-12 col-md-4">
      <div className="card h-100 shadow-sm">
        <div className="card-body">
          <div className="text-secondary small text-uppercase fw-semibold mb-1">{label}</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
          <div className="small text-secondary mt-2" style={{ lineHeight: 1.65 }}>{help}</div>
        </div>
      </div>
    </div>
  );
}

function RuleCard({ item, sectionId, whyLabel, orderLabel }) {
  const accent = sectionAccent[sectionId] || sectionAccent.futureBacklog;
  const showOrder = sectionId === 'nextUp' || sectionId === 'futureBacklog';

  return (
    <div className="card h-100 shadow-sm">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-2">
          <div>
            <div className="fw-semibold">{item.name}</div>
            <div className="small font-monospace text-secondary">{item.id}</div>
          </div>
          <span
            className="badge"
            style={{
              background: accent.background,
              border: `1px solid ${accent.border}`,
              color: accent.text,
              fontWeight: 700,
            }}
          >
            {item.status}
          </span>
        </div>
        <div className="d-flex gap-2 flex-wrap mb-3">
          {showOrder && (
            <span className="badge bg-dark-subtle text-dark border">
              {orderLabel}
            </span>
          )}
          <span className="badge bg-light text-dark border">{item.cwe}</span>
          {item.owasp && <span className="badge bg-light text-dark border">{item.owasp}</span>}
        </div>
        <div className="small mb-3" style={{ lineHeight: 1.7 }}>{item.description}</div>
        <div className="small text-secondary" style={{ lineHeight: 1.7 }}>
          <strong>{whyLabel}</strong> {item.why}
        </div>
      </div>
    </div>
  );
}

function RuleSection({ sectionId, icon, t }) {
  const items = t(`intelligenceRuleRoadmap.items.${sectionId}`, { returnObjects: true });

  if (sectionId === 'notes') {
    return (
      <section id={sectionId} style={{ marginBottom: '2.5rem', scrollMarginTop: '1.5rem' }}>
        <h2 style={{ fontSize: '1.375rem', fontWeight: 700, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>{icon}</span> {t(`intelligenceRuleRoadmap.sections.${sectionId}.title`)}
        </h2>
        <p style={{ lineHeight: 1.75 }}>{t(`intelligenceRuleRoadmap.sections.${sectionId}.body`)}</p>
        <ul style={{ lineHeight: 1.8, paddingLeft: '1.25rem', marginBottom: 0 }}>
          {t(`intelligenceRuleRoadmap.sections.${sectionId}.bullets`, { returnObjects: true }).map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>
    );
  }

  return (
    <section id={sectionId} style={{ marginBottom: '2.5rem', scrollMarginTop: '1.5rem' }}>
      <h2 style={{ fontSize: '1.375rem', fontWeight: 700, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span>{icon}</span> {t(`intelligenceRuleRoadmap.sections.${sectionId}.title`)}
      </h2>
      <p style={{ lineHeight: 1.75 }}>{t(`intelligenceRuleRoadmap.sections.${sectionId}.body`)}</p>
        <div className="row g-3">
          {items.map((item, index) => (
            <div key={item.id} className="col-12 col-xl-6">
              <RuleCard
                item={item}
                sectionId={sectionId}
                whyLabel={t('intelligenceRuleRoadmap.fields.why')}
                orderLabel={t('intelligenceRuleRoadmap.fields.order', { number: index + 1 })}
              />
            </div>
          ))}
        </div>
    </section>
  );
}

export default function IntelligenceRuleRoadmap() {
  const { t } = useTranslation();
  const liveNow = t('intelligenceRuleRoadmap.items.liveNow', { returnObjects: true });
  const nextUp = t('intelligenceRuleRoadmap.items.nextUp', { returnObjects: true });
  const futureBacklog = t('intelligenceRuleRoadmap.items.futureBacklog', { returnObjects: true });

  return (
    <div className="App-body">
      <div className="ce-page-header" style={{ paddingLeft: 0, paddingRight: 0, marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h1 className="ce-page-title">🗺️ {t('intelligenceRuleRoadmap.title')}</h1>
            <p className="ce-page-subtitle">{t('intelligenceRuleRoadmap.subtitle')}</p>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <Link className="btn btn-outline-primary" to="/docs/intelligence">
              {t('intelligenceRuleRoadmap.backToIntelligence')}
            </Link>
            <Link className="btn btn-outline-secondary" to="/docs">
              {t('intelligenceRuleRoadmap.backToDocs')}
            </Link>
          </div>
        </div>
      </div>

      <div
        style={{
          background: 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(59,130,246,0.08))',
          border: '1px solid var(--bs-border-color)',
          borderRadius: 14,
          padding: '1.25rem 1.5rem',
          marginBottom: '1.5rem',
        }}
      >
        <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--bs-secondary-color)' }}>
          {t('intelligenceRuleRoadmap.heroLabel')}
        </div>
        <p style={{ margin: '0.75rem 0 0', lineHeight: 1.75 }}>
          {t('intelligenceRuleRoadmap.heroBody')}
        </p>
      </div>

      <div className="row g-3 mb-4">
        <SummaryCard label={t('intelligenceRuleRoadmap.summary.liveNow.label')} value={liveNow.length} help={t('intelligenceRuleRoadmap.summary.liveNow.help')} />
        <SummaryCard label={t('intelligenceRuleRoadmap.summary.nextUp.label')} value={nextUp.length} help={t('intelligenceRuleRoadmap.summary.nextUp.help')} />
        <SummaryCard label={t('intelligenceRuleRoadmap.summary.futureBacklog.label')} value={futureBacklog.length} help={t('intelligenceRuleRoadmap.summary.futureBacklog.help')} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '2.5rem', alignItems: 'start' }}>
        <div className="docs-layout-toc" style={{ position: 'sticky', top: '1.5rem' }}>
          <div className="section-title" style={{ marginBottom: '0.625rem' }}>{t('intelligenceRuleRoadmap.contents')}</div>
          {sections.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              style={{
                display: 'block',
                padding: '0.3rem 0.625rem',
                borderRadius: 'var(--radius-xs)',
                fontSize: '0.8125rem',
                color: 'var(--bs-secondary-color)',
                textDecoration: 'none',
                marginBottom: '1px',
              }}
            >
              {section.icon} {t(`intelligenceRuleRoadmap.sections.${section.id}.title`)}
            </a>
          ))}
        </div>

        <div className="docs-layout-content">
          {sections.map((section) => (
            <RuleSection key={section.id} sectionId={section.id} icon={section.icon} t={t} />
          ))}

          <div
            style={{
              background: 'var(--bs-tertiary-bg)',
              border: '1px solid var(--bs-border-color)',
              borderRadius: 12,
              padding: '1rem 1.25rem',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '0.375rem' }}>{t('intelligenceRuleRoadmap.relatedTitle')}</div>
            <div style={{ lineHeight: 1.7 }}>
              {t('intelligenceRuleRoadmap.relatedBody')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
